import CardBody from 'components/Card/CardBody';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import FormLabel from '@material-ui/core/FormLabel';
import FormImageUpload from 'components/FormImageUpload/FormImageUpload';
import ColorPicker from 'components/BlockColorPicker/BlockColorPicker';
import Card from 'components/Card/Card';
import Button from 'components/CustomButtons/Button.jsx';
import { toast } from 'react-toastify';
import makeStyles from '@material-ui/styles/makeStyles';
import classNames from 'classnames';
import LabeledCheckbox from 'components/Checkbox/LabeledCheckbox';

const DEFAULT_MAIN_COLOR = '#000000';
const DEFAULT_SECONDARY_COLOR = '#B58A78';
const DEFAULT_BACKGROUND_COLOR = '#FAFBFC';

export const withBrandColorsFallbacks = module => ({
  ...module,
  mainColor: module?.mainColor || DEFAULT_MAIN_COLOR,
  secondaryColor: module?.secondaryColor || DEFAULT_SECONDARY_COLOR,
  backgroundColor: module?.backgroundColor || DEFAULT_BACKGROUND_COLOR,
  useImageAsBackground: module?.useImageAsBackground ?? false,
});

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    gap: '40px',
    '& > div': {
      maxWidth: '225px',
    },
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  label: {
    marginTop: '15px',
  },
  backgroundImageLabel: {
    marginTop: '15px',
    marginBottom: '24px',
  },
  picker: {
    marginTop: '30px',
  },
  pickerDisabled: {
    pointerEvents: 'none',
    opacity: 0.6,
  },
  imageUpload: {
    marginLeft: '-15px',
  },
  toggleButton: {
    marginTop: '15px',
  },
});

export const BackgroundSettingsFields = ({
  t,
  module,
  onChange,
  classes,
  isColor,
  setIsColor,
  disabled = false,
}) => {
  const styles = useStyles();

  const backgroundImageUrl =
    module?.['@resources']?.[module?.backgroundImage]?.contentUrl ?? null;

  const labelClassName = classNames(classes.labelHorizontal, styles.label);
  const pickerClassName = classNames(
    styles.picker,
    disabled && styles.pickerDisabled
  );

  const handleColorPicker = (color, fieldName) =>
    onChange({ ...module, [fieldName]: color.hex });

  const getImage = (fieldName, data) =>
    onChange({
      ...module,
      [fieldName]: data?.['@id'] || null,
      '@resources': {
        ...module?.['@resources'],
        ...(data?.['@id'] ? { [data['@id']]: data } : {}),
      },
    });

  const removeImage = fieldName =>
    onChange({
      ...module,
      [fieldName]: null,
      useImageAsBackground: false,
    });

  return (
    <div className={styles.wrapper}>
      <div>
        <FormLabel className={labelClassName}>
          {t('brandCfg.baseColor')}
        </FormLabel>
        <div className={pickerClassName}>
          <ColorPicker
            color={module?.mainColor}
            handleChange={color => handleColorPicker(color, 'mainColor')}
          />
        </div>
      </div>
      <div>
        <FormLabel className={labelClassName}>
          {t('brandCfg.secondaryColor')}
        </FormLabel>
        <div className={pickerClassName}>
          <ColorPicker
            color={module?.secondaryColor}
            handleChange={color => handleColorPicker(color, 'secondaryColor')}
          />
        </div>
      </div>
      <div>
        {isColor ? (
          <div>
            <FormLabel className={labelClassName}>
              {t('brandCfg.backgroundColor')}
            </FormLabel>
            <div className={pickerClassName}>
              <ColorPicker
                color={module?.backgroundColor}
                handleChange={color =>
                  handleColorPicker(color, 'backgroundColor')
                }
              />
            </div>
          </div>
        ) : (
          <div>
            <div>
              <FormLabel
                className={classNames(
                  classes.labelHorizontal,
                  styles.backgroundImageLabel
                )}
              >
                {t('brandCfg.backgroundPic')}
              </FormLabel>
              <div className={styles.imageUpload}>
                <FormImageUpload
                  stateName="backgroundImage"
                  buttonText={t('brandCfg.selectBackground')}
                  getImage={getImage}
                  removeImage={removeImage}
                  previewUrl={backgroundImageUrl}
                  isDisabled={disabled}
                />
              </div>
            </div>
            <div
              onClick={() =>
                !disabled &&
                !module?.backgroundImage &&
                toast.warning(
                  t('clientPanelSettings.backgroundSettings.warrning')
                )
              }
            >
              <LabeledCheckbox
                label={t('brandCfg.useImage')}
                checked={Boolean(module?.useImageAsBackground)}
                onChange={() =>
                  onChange({
                    ...module,
                    useImageAsBackground: !module?.useImageAsBackground,
                  })
                }
                disabled={disabled || !module?.backgroundImage}
              />
            </div>
          </div>
        )}

        <div className={styles.toggleButton}>
          <Button
            color="info"
            round={false}
            fullWidth={true}
            onClick={() => {
              setIsColor(prev => !prev);
            }}
          >
            {isColor
              ? t('brandCfg.clientPanelDesign.changeToImage')
              : t('brandCfg.clientPanelDesign.changeToColor')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const BackgroundSettings = ({
  t,
  module,
  onChange,
  classes,
  isColor,
  setIsColor,
}) => (
  <Card>
    <CardBody>
      <GridContainer>
        <GridItem sm={12}>
          <BackgroundSettingsFields
            t={t}
            module={module}
            onChange={onChange}
            classes={classes}
            isColor={isColor}
            setIsColor={setIsColor}
          />
        </GridItem>
      </GridContainer>
    </CardBody>
  </Card>
);

export default BackgroundSettings;
